import { useState } from "react"
import { produce } from "immer"
import { HxMapGroup } from "../../../../Comps/HxMap/HxMapGroup"
import { NotesSelectMenu } from "../../../../Comps/NoteButtons/NotesSelect"
import { HexMap } from "../../../../Data/HexMap/HexMap"
import { HexMapBuilderParams, BuildHexMap } from "../../../../Data/HexMap/HexMapBuilder"
import { HexMapModifier } from "../../../../Data/HexMap/HexMapModifier"
import { Note } from "../../../../Data/HexMap/Note"
import { FlexRowTwo } from "../../../../Layouts/FlexRows"
import { useNoteNames } from "../../../../Project/ProjectStore"
import { All } from "../../../../Util/Constants/Quantities"
import { Music, useScaleNotes } from "../../../../Util/Music/Music"
import { TransformTranslate } from "../../../../Util/Transform/Transform"
import { IconEye } from "../../../../Comps/Icons/IconEye"
import { CommonNotes } from "../CommonNotes"
import { CommonChords } from "../CommonChords"

export function CycleOfSecondsPair(){ 
    const [tonic, setTonic] = useState(Music.G)
    const getAdjustedTonic = (t:number) => { return (t - 4) % 12 }
    const getMap = () => {
        const params:HexMapBuilderParams = {rows:2, columns:3, tonic:getAdjustedTonic(tonic), radius:80}
        const map:HexMap = BuildHexMap(params)
        const mod:HexMapModifier = new HexMapModifier(map)
        //mod.keys_setOpen([0,2], true)
        mod.keys_setOpen([0,1,4,5], false)
        mod.keys_setVisible([0,1,4,5], false)
        //mod.notes_setPrimaryLabelAsNoteIndex([All])
        return map
    }
    const [map, setMap] = useState<HexMap>(getMap())
    const [width,height] = [500,400]
    const onNoteClick = (note:Note) => {
        setMap(produce((draft:HexMap)=>{
            const mod:HexMapModifier = new HexMapModifier(draft)
            mod.notes_advanceSelectedState([note.params.noteIndex])
        }))
    }
    const resetTonic = (t:number) => {
        setTonic(t)
        setMap(produce((draft:HexMap)=>{
            const mod:HexMapModifier = new HexMapModifier(draft)
            mod.setTonic(getAdjustedTonic(t))
        }))
    }
    const commonNoteIndexes:number[] = [12, 13, 8, 9, 15, 10, 17, 18, 19, 20]
    const showCommonNotes = () => {
        const noteIndexes:number[] = commonNoteIndexes
        setMap(produce((draft:HexMap)=>{
            const mod:HexMapModifier = new HexMapModifier(draft)
            mod.notes_setDefault([All])
            mod.notes_setSelected(noteIndexes)
        }))
    }
    const commonChordIndexes:number[] = [8,13,17,18]
    const showCommonChords = () => {
        const noteIndexes:number[] = commonChordIndexes
        setMap(produce((draft:HexMap)=>{
            const mod:HexMapModifier = new HexMapModifier(draft)
            mod.notes_setDefault([All])
            mod.notes_setHilited(noteIndexes)
        }))
    }
    const showNotes = (noteIndexes:number[]) => {
        setMap(produce((draft:HexMap)=>{
            const mod:HexMapModifier = new HexMapModifier(draft)
            mod.notes_setDefault([All])
            mod.notes_setHilited(noteIndexes)
        }))
    }
    const noteNames:string[] = useNoteNames()
    const leftTonicNote:string = noteNames[tonic]
    const rightTonicNote:string = noteNames[(tonic + 2) % 12]
    const scale:string[] = useScaleNotes(tonic)
    const rightScale:string[] = useScaleNotes((tonic + 4) % 12)
    const clear = () => {
        setMap(getMap())
    }
    return(
        <div className={'harmonic-cycle-pair'}>
            <FlexRowTwo columnOneFlex={1} columnTwoFlex={1}>
               <div className={'centered'} style={{marginTop:30}}> 
                    <div><NotesSelectMenu onChange={resetTonic} tonic={tonic}/></div>
                    <svg width={width} height={height} className={'pair-hexmap'}>
                        <g transform={TransformTranslate(width/2.5, height/2)}>
                            <HxMapGroup hexMap={map} onNoteClick={onNoteClick}/>
                        </g>
                    </svg>
                    <div><button style={{marginTop:25}} type='button' onClick={clear}>Clear</button></div>
                </div>
                <div className={'pair-description'}>
                    <h2>Properties of Two Keys Along a Cycle of Flat Thirds</h2>
                    <ul>
                        <li>Common Notes: 5 of 7 <CommonNotes map={map} noteIndexes={commonNoteIndexes}/> <IconEye onClick={showCommonNotes}/></li>
                        <li>Common Triadic Chords: 2 of 7<CommonChords map={map} noteIndexes={commonChordIndexes}/><IconEye onClick={showCommonChords}/></li>
                        <li>The top right key ({rightTonicNote}) is a second (2 notes) higher than the bottom left key ({leftTonicNote}).</li>
                        <li>Conversely, the bottom left key ({leftTonicNote}) is a flat seventh (10 notes) higher than the top right key ({rightTonicNote})</li>   
                    </ul>
                    <h2>Common Modulation Techniques:</h2>
                    <ul>
                        <li>Pivot Chords {scale[2]} minor and {scale[4]} major</li>
                        <li>Chain of Minor Chords: {scale[1]}, {scale[2]}, {rightScale[1]}</li>
                        <li>Chain of Major Chords: {scale[3]}, {scale[4]}, {scale[5]} </li>
                    </ul>
                    <h2>Parallel Modes (top left key to bottom right key):</h2>
                    <ul>
                        <li>
                            {scale[1]} Dorian in {leftTonicNote} and {scale[1]} Ionian (major) in {rightTonicNote} 
                            <IconEye onClick={()=>{showNotes([10,12])}}/>
                        </li>
                        <li>
                            {scale[2]} Phrygian in {leftTonicNote} and {scale[2]} Dorian in {rightTonicNote} 
                            <IconEye onClick={()=>{showNotes([17,13])}}/>
                        </li>
                        <li>
                            {scale[4]} Mixolydian in {leftTonicNote} and {scale[4]} Lydian in {rightTonicNote} 
                            <IconEye onClick={()=>{showNotes([18,8])}}/>
                        </li>
                        <li>
                            {scale[5]} Aeolian (minor) in {leftTonicNote} and {scale[5]} Mixolydian in {rightTonicNote} 
                            <IconEye onClick={()=>{showNotes([19,9])}}/>
                        </li>
                        <li>
                            {scale[6]} Locrian in {leftTonicNote} and {scale[6]} Aeolian (minor) in {rightTonicNote} 
                            <IconEye onClick={()=>{showNotes([20,15])}}/>
                        </li>
                    </ul>
                </div>
            </FlexRowTwo>

        </div>
    )
}